/**
 * Shared formatting helpers for energy plan display.
 *
 * Used by bill-review-step and energy-plan-prompt components to build
 * consistent plan subtitle text (date range or "Draft Plan").
 */

/**
 * Formats a single plan date as M/D/YYYY.
 * Returns '?' for falsy or unparseable values.
 *
 * Plan dates are calendar dates (no time component). ISO strings like
 * "2023-04-01" must NOT go through `new Date(string)` — that parses them as
 * UTC midnight, which renders as the previous day in western timezones.
 */
export function formatPlanDate(date: unknown): string {
  if (!date) return '?';
  if (typeof date === 'string') {
    const ymd = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) {
      return `${Number(ymd[2])}/${Number(ymd[3])}/${ymd[1]}`;
    }
  }
  const d = new Date(date as string | number);
  if (isNaN(d.getTime())) return '?';
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

/**
 * Builds a subtitle string for an energy plan option.
 * Shows "Draft Plan" when no end date exists, otherwise shows "startDate - endDate".
 */
export function buildPlanSubtitle(plan: { planStartDate?: unknown; planEndDate?: unknown }): string {
  if (plan.planEndDate == null) {
    return 'Draft Plan';
  }
  const start = formatPlanDate(plan.planStartDate);
  const end = formatPlanDate(plan.planEndDate);
  return `${start} - ${end}`;
}
